<template>
  <v-card v-if="loaded" class="mt-3" color="blue-grey lighten-5" elevation="0">
    <v-card-text>
      <v-row>
        <v-col lg="3" md="6" cols="12">
          <v-card elevation="1" height="280">
            <v-card-title class="text-h5 d-flex justify-space-between">
              <div class="d-flex align-center">
                {{ $t('performance_dashboard_ui.root_causes_analyzed') }}
                <info-modal width="800">
                  <v-card-title class="text-h5">
                    {{ $t('performance_dashboard_ui.modal_info.route_assessments.main.title') }}
                  </v-card-title>
                  <v-card-text v-html="$t('performance_dashboard_ui.modal_info.route_assessments.main.text')"/>
                </info-modal>
              </div>
              <v-btn
                v-if="singleCustomerMatches"
                color="primary"
                :to="{
                  name: 'route-index',
                  query: {
                    startedAfter: Moment().subtract(30, 'days').format('YYYY-MM-DD'),
                    startedBefore: Moment().format('YYYY-MM-DD'),
                    excludeOnTimeViolations: 'true'
                  }
                }">
                <v-icon>mdi-archive</v-icon>
                {{ $t('late_rca') }}
              </v-btn>
            </v-card-title>
            <v-card-subtitle class="text-truncate">
              ({{ timePeriodLabel }})
            </v-card-subtitle>
            <v-divider></v-divider>
            <v-card-text>
              <div class="text-subtitle-1" v-if="overallStatus.totalAssessments === 0">
                {{ $t('no_data') }}
              </div>
              <v-list v-else>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-check-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('on_time') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="success" dark>
                      {{ overallStatus.assessed.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-alert-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('incomplete_or_late') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="danger" dark>
                      {{ overallStatus.unassessed.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col>
          <v-card elevation="1" height="280">
            <v-card-title class="text-h5 d-flex justify-space-between">
              <div class="d-flex align-center">
                <span>
                  {{ $t('performance_dashboard_ui.daily_trend') }}
                  <span class="subheader hidden-md-only">{{ $t('performance_dashboard_ui.trailing_six_months') }}</span>
                </span>
                <info-modal width="800">
                  <v-card-title class="text-h5">
                    {{ $t('performance_dashboard_ui.modal_info.route_assessments.chart.title') }}
                  </v-card-title>
                  <v-card-text v-html="$t('performance_dashboard_ui.modal_info.route_assessments.chart.text')"/>
                </info-modal>
              </div>
              <v-btn color="primary" @click="generateReport">
                <v-icon>mdi-chart-areaspline</v-icon>
                {{ $t('performance_dashboard_ui.see_full_report') }}
              </v-btn>
            </v-card-title>
            <v-card-text>
              <root-cause-analysis-chart ref="trailing-assessments-chart" :selected-customers="selectedCustomers"/>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-card v-if="filteredCustomers.length > 1" class="mt-4" elevation="1">
        <v-card-title class="text-h5">
          {{ $t('performance_dashboard_ui.fewest_analyzed_incidents') }}
          <info-modal width="800">
            <v-card-title class="text-h5">
              {{ $t('performance_dashboard_ui.modal_info.route_assessments.distribution_centers.title') }}
            </v-card-title>
            <v-card-text v-html="$t('performance_dashboard_ui.modal_info.route_assessments.distribution_centers.text')"/>
          </info-modal>
        </v-card-title>
        <v-card-subtitle>
          ({{ timePeriodLabel }})
        </v-card-subtitle>
        <v-divider></v-divider>
        <v-data-table
          class="vf-table"
          :items="filteredCustomers"
          :headers="tableFields"
          sort-by="percent_assessed">
          <template #item.name="row">
            <router-link :to="`/performance/dashboard/customers/${row.item.id}`">{{ row.item.name }}</router-link>
          </template>
          <template #item.percent_assessed="row">
            {{ round(row.value, 1) }}%
          </template>
          <template #item.assessed="row">
            {{ row.value.toLocaleString() }}
          </template>
          <template #item.unassessed="row">
            {{ row.value.toLocaleString() }}
          </template>
        </v-data-table>
      </v-card>
    </v-card-text>
  </v-card>
  <v-card v-else class="mt-3" color="blue-grey lighten-5" elevation="0">
    <v-card-text>
      <v-row>
        <v-col cols="12" md="6" lg="3">
          <v-skeleton-loader type="card" />
        </v-col>
        <v-col>
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-skeleton-loader type="table"/>
  </v-card>
</template>

<script>
import RootCauseAnalysisChart from '@/components/performance-dashboard/RootCauseAnalysisChart'
import InfoModal from '@/components/shared/InfoModal'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import PerformanceDashboardComponentMixin from '@/components/performance-dashboard/PerformanceDashboardComponentMixin'
import MathUtil from '@/util/MathUtil'
import Moment from 'moment-timezone'
import { each, filter, invoke, map, sortBy, uniq, round, reverse, size } from 'lodash'

export default {
  name: 'RootCauseDashboard',
  components: {
    RootCauseAnalysisChart,
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    PerformanceDashboardComponentMixin
  ],
  data () {
    return {
      loaded: false,
      routeAssessmentsData: null,
      tableFields: [
        {
          value: 'name',
          text: this.$tc('name', 2)
        },
        {
          value: 'percent_assessed',
          text: this.$t('performance_dashboard_ui.percent_analyzed')
        },
        {
          value: 'assessed',
          text: this.$t('performance_dashboard_ui.incidents_analyzed')
        },
        {
          value: 'unassessed',
          text: this.$t('incomplete')
        }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    round: round,
    Moment,
    /**
     * Load the data from the API.
     */
    async loadData () {
      this.loaded = false
      this.$emit('loading')

      const requestedCustomerIds = this.requestedCustomerIds.join(',')

      try {
        // Retrieve the temperature violations data
        const result = await this.$httpAdapter.get('/customer-insights/route-assessments', {
          params: {
            customer_id: requestedCustomerIds
          }
        })
        this.routeAssessmentsData = result.data
        this.$nextTick(() => {
          this.$emit('loaded', {
            value: this.overallStatus.percentAssessed
          })
        })
      } catch (error) {
        this.$emit('errorLoading')
        this.handleError(error)
      } finally {
        this.loaded = true
      }
    },
    /**
     * Generate the full Pending Route Assessments report and redirect to it.
     */
    async generateReport () {
      const reportParameters = {}

      reportParameters.startDate = Moment.tz(this.customerTimezone).subtract(6, 'months').format('YYYY-MM-DD')
      reportParameters.endDate = Moment.tz(this.customerTimezone).format('YYYY-MM-DD')
      reportParameters.customerIds = map(this.selectedCustomers, x => x.id)

      const report = this.$orm('Report').createRecord({
        type: 'RootCauseAnalysisHistoryReport',
        name: this.$t('performance_dashboard_ui.root_cause_analysis_history_report'),
        parameters: reportParameters
      })

      const savedReport = await this.$orm('Report').createOrUpdate(report)
      await this.$router.push(`/report/${savedReport.id}`)
    }
  },
  computed: {
    /**
     * Depending on whichever time frames various customers use, we want to present the "(past X days)" label
     * differently.
     */
    timePeriodLabel () {
      const start = uniq(map(this.routeAssessmentsData, x => x.start))
      const end = uniq(map(this.routeAssessmentsData, x => x.end))

      // If we have precisely 1 time frame, we can use a specific label here.
      if (size(start) === 1 && size(end) === 1) {
        if (start[0] === '-30 days' && end[0] === 'now') {
          return this.$t('past_30_days')
        } else if (start[0] === '-30 days' && end[0] === '-7 days') {
          return this.$t('n_through_m', {
            n: Moment.tz(this.customerTimezone).subtract(30, 'days').format('LL'),
            m: Moment.tz(this.customerTimezone).subtract(7, 'days').format('LL')
          })
        }
      }
      // Otherwise, use a generic label.
      return this.$t('past_time_period')
    },
    /**
     * The data object used for the 'Overall Health' section.
     * @returns Object|null
     */
    overallStatus () {
      if (this.loaded && this.routeAssessmentsData != null) {
        const results = {
          totalAssessments: 0,
          assessed: 0,
          unassessed: 0,
          percentAssessed: 0
        }
        each(this.routeAssessmentsData, (customer) => {
          results.totalAssessments += customer.total
          results.assessed += customer.assessed
          results.unassessed += customer.unassessed
        })
        results.percentAssessed = results.totalAssessments === 0 ? 100 : MathUtil.percentage(results.assessed, results.totalAssessments, null)
        return results
      }
      return null
    },
    /**
     * A list of customers that:
     * 1) Has had customer ids and names added in (since these aren't returned with the API)
     * 2) Has customers with no vehicles filtered out
     * 3) Has been sorted by the percentage assessed
     */
    filteredCustomers () {
      const mapFn = (customer, customerId) => {
        customer.id = parseInt(customerId)
        customer.name = this.getCustomerName(customerId)
        return customer
      }
      // Filter out all customers with no data
      const filterFn = (customer) => {
        return customer.total > 0
      }
      const filteredCustomers = filter(map(this.routeAssessmentsData, mapFn), filterFn)
      // Add an extra layer of sorting where we sort by the total.
      // Debatable that we need to do this, but some customers are really bad about assessing stuff so we could have a
      // bunch of customers sitting at 0%, and this will bubble up the worst offenders.
      return reverse(sortBy(filteredCustomers,
        [
          c => 100 - c.percent_assessed,
          c => c.total
        ]
      ))
    },
    /**
     * Determines if only a single customer has been selected AND that customer matches what is in the customer context.
     */
    singleCustomerMatches () {
      if (this.requestedCustomerIds.length === 1) {
        return this.requestedCustomerIds[0] === this.customerContextId
      }
      return false
    }
  },
  watch: {
    selectedCustomers () {
      this.loadData()
    },
    // When tab becomes active, force the charts to redraw (in case the window size changed while on another tab)
    active (newValue, oldValue) {
      if (newValue === true) {
        invoke(this.$refs, 'trailing-assessments-chart.fit')
      }
    }
  }
}
</script>

<style scoped>
.subheader {
  font-weight: normal;
  font-size: 18px;
  opacity: .8;
}
</style>
